import { addDays, isIsoDate } from "./dates";
import { DomainError } from "./errors";

// ===== Boleto de cobrança (T2.10, RF-FIN-04): código de barras, linha digitável e campo livre =====
//
// Padrão Febraban (boleto de cobrança, 44 posições no código de barras e 47 na linha digitável):
//   código de barras = banco(3) moeda(1) DV(1) fator de vencimento(4) valor(10) campo livre(25)
//   linha digitável  = campo 1 (banco, moeda, campo livre 1–5, DV mód. 10) · campo 2 (campo livre 6–15, DV) ·
//                      campo 3 (campo livre 16–25, DV) · campo 4 (DV geral) · campo 5 (fator + valor)
// O DV geral é o módulo 11 com pesos 2–9 (resto 0, 10 ou 11 → 1); os DVs dos campos, o módulo 10 com pesos 2/1.

/** Valor que cabe no campo de 10 dígitos do código de barras (R$ 99.999.999,99). */
export const BOLETO_MAX_CENTS = 9_999_999_999;
/** Moeda real no código de barras. */
export const BOLETO_CURRENCY = "9";
/** Banco fictício do provedor de testes: a linha tem DVs válidos, mas nenhum banco a aceita (como o `.invalid` do Pix). */
export const SANDBOX_BANK_CODE = "999";

const invalid = (msg: string): never => {
  throw new DomainError("invalid_boleto", msg);
};
const digits = (s: string, n: number, what: string) => {
  if (!new RegExp(`^\\d{${n}}$`).test(s)) invalid(`${what}: esperados ${n} dígitos.`);
  return s;
};

/** Módulo 10 (pesos 2, 1, 2… da direita para a esquerda; produto ≥ 10 soma os algarismos). */
export function mod10(s: string): number {
  if (!/^\d+$/.test(s)) invalid("Só dígitos no cálculo do módulo 10.");
  let sum = 0;
  let w = 2;
  for (let i = s.length - 1; i >= 0; i--) {
    const p = Number(s[i]) * w;
    sum += p > 9 ? p - 9 : p;
    w = w === 2 ? 1 : 2;
  }
  return (10 - (sum % 10)) % 10;
}

/** Módulo 11 com pesos 2–9 (da direita para a esquerda); devolve o resto da divisão por 11. */
export function mod11Rest(s: string): number {
  if (!/^\d+$/.test(s)) invalid("Só dígitos no cálculo do módulo 11.");
  let sum = 0;
  let w = 2;
  for (let i = s.length - 1; i >= 0; i--) {
    sum += Number(s[i]) * w;
    w = w === 9 ? 2 : w + 1;
  }
  return sum % 11;
}

/** DV geral do código de barras (43 posições sem o DV): 11 − resto; 0, 10 e 11 viram 1. */
export function barcodeCheckDigit(without: string): number {
  digits(without, 43, "Código de barras sem o DV");
  const dv = 11 - mod11Rest(without);
  return dv === 0 || dv === 10 || dv === 11 ? 1 : dv;
}

// ---------- fator de vencimento ----------

/** Base do fator (07/10/1997 = 0) e a virada da Febraban: 21/02/2025 = 9999, 22/02/2025 volta a 1000. */
const FACTOR_BASE = "1997-10-07";
const FACTOR_RESTART = "2025-02-22";
const DAY_MS = 86_400_000;
const dayNumber = (iso: string) => {
  if (!isIsoDate(iso)) invalid(`Data inválida: ${iso}`);
  return Math.round(Date.parse(`${iso}T00:00:00Z`) / DAY_MS);
};

/**
 * Fator de vencimento (4 dígitos) da data. Até 21/02/2025, dias desde 07/10/1997 (a partir de 1000 = 03/07/2000); de
 * 22/02/2025 em diante recomeça em 1000 e repete a cada 9.000 dias.
 */
export function dueFactor(dueDate: string): string {
  const days = dayNumber(dueDate) - dayNumber(FACTOR_BASE);
  if (days < 1000) invalid("Vencimento anterior a 03/07/2000.");
  const f = days <= 9999 ? days : ((dayNumber(dueDate) - dayNumber(FACTOR_RESTART)) % 9000) + 1000;
  return String(f).padStart(4, "0");
}

/**
 * Vencimento do fator, escolhendo o ciclo mais próximo de `reference` (hoje, em geral). "0000" = sem vencimento.
 */
export function dueDateFromFactor(factor: string, reference: string): string | null {
  digits(factor, 4, "Fator de vencimento");
  const f = Number(factor);
  if (f === 0) return null;
  if (f < 1000) invalid("Fator de vencimento inválido.");
  const ref = dayNumber(reference);
  const candidates = [addDays(FACTOR_BASE, f)];
  for (let k = 0; k < 4; k++) candidates.push(addDays(FACTOR_RESTART, f - 1000 + 9000 * k));
  let best = candidates[0]!;
  for (const c of candidates) if (Math.abs(dayNumber(c) - ref) < Math.abs(dayNumber(best) - ref)) best = c;
  return best;
}

// ---------- código de barras e linha digitável ----------

export type BarcodeInput = {
  /** Código do banco (3 dígitos). */
  bankCode: string;
  dueDate: string;
  amountCents: number;
  /** Campo livre do banco (25 dígitos). */
  freeField: string;
};

/** Código de barras de 44 dígitos com o DV geral. */
export function buildBarcode(i: BarcodeInput): string {
  digits(i.bankCode, 3, "Banco");
  digits(i.freeField, 25, "Campo livre");
  if (!Number.isSafeInteger(i.amountCents) || i.amountCents < 0 || i.amountCents > BOLETO_MAX_CENTS)
    invalid("Valor do boleto fora do limite.");
  const without = `${i.bankCode}${BOLETO_CURRENCY}${dueFactor(i.dueDate)}${String(i.amountCents).padStart(10, "0")}${i.freeField}`;
  const dv = barcodeCheckDigit(without);
  return `${without.slice(0, 4)}${dv}${without.slice(4)}`;
}

export type ParsedBarcode = {
  bankCode: string;
  currency: string;
  checkDigit: number;
  factor: string;
  amountCents: number;
  freeField: string;
};

/** Lê e confere o código de barras (44 dígitos, moeda 9, DV geral). Inválido = `invalid_boleto`. */
export function parseBarcode(barcode: string): ParsedBarcode {
  digits(barcode, 44, "Código de barras");
  const without = barcode.slice(0, 4) + barcode.slice(5);
  if (barcode[3] !== BOLETO_CURRENCY) invalid("Moeda do boleto diferente de real.");
  if (Number(barcode[4]) !== barcodeCheckDigit(without)) invalid("DV do código de barras não confere.");
  return {
    bankCode: barcode.slice(0, 3),
    currency: barcode[3]!,
    checkDigit: Number(barcode[4]),
    factor: barcode.slice(5, 9),
    amountCents: Number(barcode.slice(9, 19)),
    freeField: barcode.slice(19),
  };
}

/** Linha digitável (47 dígitos, sem pontuação) do código de barras. */
export function barcodeToDigitableLine(barcode: string): string {
  const b = parseBarcode(barcode);
  const f1 = `${b.bankCode}${b.currency}${b.freeField.slice(0, 5)}`;
  const f2 = b.freeField.slice(5, 15);
  const f3 = b.freeField.slice(15, 25);
  return `${f1}${mod10(f1)}${f2}${mod10(f2)}${f3}${mod10(f3)}${b.checkDigit}${b.factor}${String(b.amountCents).padStart(10, "0")}`;
}

/**
 * Linha digitável (com ou sem pontuação e espaços) → código de barras, conferindo os três DVs de campo e o DV geral.
 * Qualquer divergência = `invalid_boleto`.
 */
export function digitableLineToBarcode(line: string): string {
  const s = line.replace(/[\s.]/g, "");
  digits(s, 47, "Linha digitável");
  const f1 = s.slice(0, 9);
  const f2 = s.slice(10, 20);
  const f3 = s.slice(21, 31);
  if (mod10(f1) !== Number(s[9]) || mod10(f2) !== Number(s[20]) || mod10(f3) !== Number(s[31]))
    invalid("DV de campo da linha digitável não confere.");
  const barcode = `${s.slice(0, 4)}${s[32]}${s.slice(33, 47)}${s.slice(4, 9)}${f2}${f3}`;
  parseBarcode(barcode);
  return barcode;
}

export const isValidDigitableLine = (line: string): boolean => {
  try {
    digitableLineToBarcode(line);
    return true;
  } catch {
    return false;
  }
};

export const isValidBarcode = (barcode: string): boolean => {
  try {
    parseBarcode(barcode);
    return true;
  } catch {
    return false;
  }
};

/** "00190.50095 40144.816069 06809.350314 3 37370000000100" (formato impresso). */
export function formatDigitableLine(line: string): string {
  const s = line.replace(/[\s.]/g, "");
  digits(s, 47, "Linha digitável");
  return `${s.slice(0, 5)}.${s.slice(5, 10)} ${s.slice(10, 15)}.${s.slice(15, 21)} ${s.slice(21, 26)}.${s.slice(26, 32)} ${s[32]} ${s.slice(33)}`;
}

// ---------- Banco do Brasil, convênio de 7 dígitos (carteira própria, T2.10) ----------

export const BB_BANK_CODE = "001";
/** Sequencial do nosso número no convênio de 7 dígitos (10 posições). */
export const BB_MAX_OUR_NUMBER_SEQ = 9_999_999_999;

/** Nosso número do BB com convênio de 7 dígitos: convênio + sequencial de 10 (17 dígitos, sem DV). */
export function bbOurNumber(agreement: string, seq: number): string {
  digits(agreement, 7, "Convênio");
  if (!Number.isSafeInteger(seq) || seq < 1 || seq > BB_MAX_OUR_NUMBER_SEQ)
    invalid("Sequencial do nosso número fora da faixa.");
  return `${agreement}${String(seq).padStart(10, "0")}`;
}

/** Campo livre do BB, convênio de 7 dígitos: "000000" + nosso número (17) + carteira (2). */
export function bbFreeField(ourNumber: string, wallet: string): string {
  digits(ourNumber, 17, "Nosso número");
  digits(wallet, 2, "Carteira");
  return `000000${ourNumber}${wallet}`;
}

/** Boleto completo (código de barras e linha) de um banco a partir do campo livre. */
export function buildBoleto(i: BarcodeInput): { barcode: string; digitableLine: string } {
  const barcode = buildBarcode(i);
  return { barcode, digitableLine: barcodeToDigitableLine(barcode) };
}

// ---------- Interleaved 2 of 5 (impressão do código de barras) ----------

const I25: readonly string[] = [
  "nnwwn",
  "wnnnw",
  "nwnnw",
  "wwnnn",
  "nnwnw",
  "wnwnn",
  "nwwnn",
  "nnnww",
  "wnnwn",
  "nwnwn",
];

/**
 * Larguras das barras e espaços do código de barras (Interleaved 2 of 5, 44 dígitos): início `nnnn`, pares de dígitos
 * (barras do primeiro intercaladas com os espaços do segundo) e fim `wnn`. Começa por barra; `n` = estreita, `w` =
 * larga.
 */
export function interleaved2of5(barcode: string): ("n" | "w")[] {
  if (!/^\d+$/.test(barcode) || barcode.length % 2 !== 0)
    invalid("Código de barras inválido para impressão.");
  const out: ("n" | "w")[] = ["n", "n", "n", "n"];
  for (let i = 0; i < barcode.length; i += 2) {
    const bars = I25[Number(barcode[i])]!;
    const spaces = I25[Number(barcode[i + 1])]!;
    for (let k = 0; k < 5; k++) out.push(bars[k] as "n" | "w", spaces[k] as "n" | "w");
  }
  out.push("w", "n", "n");
  return out;
}

// ---------- vencimento e valor do boleto da conta ----------

/** Dias somados a hoje para o vencimento do boleto de uma conta já vencida (o boleto não nasce vencido). */
export const OVERDUE_BOLETO_DAYS = 3;
/** Dias depois do vencimento em que o ERP considera o boleto vivo (cobranças vivas por conta; varredura no PSP). */
export const BOLETO_ALIVE_DAYS = 30;

/** Vencimento do boleto: o da conta ou, se já passou, hoje + 3 dias. */
export function boletoDueDate(titleDueDate: string, today: string): string {
  return titleDueDate >= today ? titleDueDate : addDays(today, OVERDUE_BOLETO_DAYS);
}
